import { useEffect, useMemo, useRef, useState } from "react";
import { Check, X } from "lucide-react";
import { cn } from "./ui";
import { tagColors } from "./tags-meta";
import type { Tag } from "./contacts-api";
import { t } from "@/lib/i18n";

/* Выбор тегов контакта: выбранные — цветные чипы с крестиком, добавление — через выпадающий список с поиском.
   Список тегов большой (теги Books: «Lead: …», «Dept: …», сегменты), поэтому все теги сразу не показываем. */

const colorOf = (id: string) => tagColors.find((c) => c.id === id) ?? tagColors[0];
const tint = (hex: string) => `${hex}22`;

type Props = {
  all: Tag[];
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
};

export default function TagPicker({ all, value, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const root = useRef<HTMLDivElement | null>(null);
  const input = useRef<HTMLInputElement | null>(null);

  const chosen = value.map((id) => all.find((x) => x.id === id)).filter((x): x is Tag => Boolean(x));
  const options = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all
      .filter((x) => !value.includes(x.id))
      .filter((x) => !needle || `${x.name} ${x.emoji} ${x.description}`.toLowerCase().includes(needle))
      .sort((a, b) => {
        // точное начало названия — выше, затем популярные
        if (needle) {
          const sa = a.name.toLowerCase().startsWith(needle) ? 0 : 1;
          const sb = b.name.toLowerCase().startsWith(needle) ? 0 : 1;
          if (sa !== sb) return sa - sb;
        }
        return b.count - a.count || a.name.localeCompare(b.name);
      })
      .slice(0, 60);
  }, [all, value, q]);

  useEffect(() => setCursor(0), [q, open]);

  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const add = (id: string) => {
    onChange([...value, id]);
    setQ("");
    input.current?.focus();
  };
  const remove = (id: string) => onChange(value.filter((x) => x !== id));

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      setOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const hit = options[cursor];
      if (hit) add(hit.id);
    } else if (e.key === "Backspace" && !q && value.length) {
      remove(value[value.length - 1]);
    }
  };

  return (
    <div ref={root} className="relative">
      <div className="flex flex-wrap items-center gap-1">
        {chosen.map((x) => {
          const c = colorOf(x.color);
          return (
            <span
              key={x.id}
              className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-medium"
              style={{ backgroundColor: tint(c.hex), borderColor: tint(c.hex), color: c.hex }}
            >
              {x.emoji ? <span aria-hidden>{x.emoji}</span> : null}
              {x.name}
              {!disabled ? (
                <button type="button" onClick={() => remove(x.id)} aria-label={t("Снять тег «{name}»", { name: x.name })} className="-me-0.5 grid size-3.5 cursor-pointer place-items-center rounded-full opacity-70 transition hover:opacity-100">
                  <X className="size-2.5" strokeWidth={3} aria-hidden />
                </button>
              ) : null}
            </span>
          );
        })}
        {!disabled ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-haspopup="listbox"
            className={cn(
              "inline-flex cursor-pointer items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-[10.5px] font-medium text-muted-foreground transition hover:border-primary hover:text-primary",
              open && "border-primary text-primary",
            )}
          >
            <i className="ri-add-line text-[12px] leading-none" aria-hidden />
            {t("Добавить тег")}
          </button>
        ) : null}
        {chosen.length === 0 && disabled ? <span className="text-[11px] text-muted-foreground">{t("Тегов нет")}</span> : null}
      </div>

      {open ? (
        <div className="absolute start-0 top-full z-20 mt-1 w-full min-w-[16rem] max-w-[22rem] rounded-btn border border-border bg-card p-1 shadow-lift">
          <input
            ref={input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder={t("Найти тег…")}
            aria-label={t("Найти тег")}
            className="h-8 w-full box-border rounded-[5px] border border-border bg-background px-2 text-[12px] text-foreground placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none"
          />
          <ul role="listbox" className="mt-1 max-h-56 overflow-y-auto">
            {options.map((x, i) => {
              const c = colorOf(x.color);
              return (
                <li key={x.id} role="option" aria-selected={i === cursor}>
                  <button
                    type="button"
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => add(x.id)}
                    className={cn("flex w-full cursor-pointer items-center gap-2 rounded-[5px] px-2 py-1.5 text-start text-[12px] transition", i === cursor && "bg-muted")}
                  >
                    <span className="grid size-4 flex-none place-items-center rounded-full text-[10px] leading-none" style={{ backgroundColor: tint(c.hex), color: c.hex }} aria-hidden>
                      {x.emoji || <Check className="size-2.5" strokeWidth={3} />}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{x.name}</span>
                    {x.count ? <span className="flex-none text-[10.5px] text-muted-foreground">{x.count}</span> : null}
                  </button>
                </li>
              );
            })}
            {options.length === 0 ? (
              <li className="px-2 py-2 text-[11.5px] text-muted-foreground">
                {all.length === value.length ? t("Все теги уже выбраны.") : t("Ничего не найдено — новые теги заводятся в окне «Теги».")}
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
